import { useCallback, useMemo } from 'react'

import {
  InfiniteData,
  Mutation,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { compact, some } from 'lodash'

import { MUTATION_KEYS, QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_TRANSACTIONS } from '~/constants/refetch-intervals'
import {
  SUBMITTED_TRANSACTION_STATUSES,
  QUEUE_TRANSACTION_STATUSES,
  CONFIRMED_TRANSACTION_STATUSES,
  PENDING_TRANSACTION_STATUSES,
} from '~/constants/smart_accounts'
import { useNotifications } from '~/context/notificationContext'
import { useActiveOrgId, useActiveOrgMember } from '~/hooks/org'
import {
  ApproveTransactionParams,
  PollSubmittedTransactionParams,
  SubmitTransactionParams,
} from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import { useAccountingUpdates } from '~/hooks/useAccountingUpdates'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useConnectedAddress } from '~/hooks/useConnectedAddress'
import { useGetTransaction } from '~/hooks/useGetTransaction'
import { useServerRequests } from '~/hooks/useRequests'
import {
  useSmartAccounts,
  useTransactionSmartAccount,
} from '~/hooks/useSmartAccounts'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import {
  TransactionStatus,
  BackendTransaction,
  MetadataType,
  OrgAssetTransfer,
  OrgTransaction,
  OrgSmartAccount,
  OrgMember,
  SmartAccountRole,
  OrgMemberRole,
} from '~/types'
import { InfiniteTransactionsResponse } from '~/types/query-types'
import { Address } from '~/utils/address'
import { isTransactionOrg } from '~/utils/smartAccounts'

// Stop polling for bridge settlement after this long so a bridge that never
// settles server-side doesn't poll forever.
const BRIDGE_SETTLEMENT_POLL_CUTOFF_MS = 30 * 60 * 1000

// A terminal bridge transaction is not immutable: the backend flips each
// bridge payload's status from PENDING to SUCCESS/FAILURE (and writes the
// destination or refund transaction) after the source-chain transaction has
// already succeeded. Check every bridge leg — batched transactions can carry
// more than one.
const hasUnsettledBridge = (transaction: BackendTransaction) => {
  if (transaction.isMerkleTransaction) return false
  return (transaction.transactionMetadata ?? []).some(
    (payload) =>
      payload.type === MetadataType.Bridge &&
      (!payload.status || payload.status === 'PENDING'),
  )
}

export const isTransactionSettled = (
  transaction: BackendTransaction | undefined,
) => {
  if (!transaction) return false
  if (PENDING_TRANSACTION_STATUSES.includes(transaction.status)) return false
  return !hasUnsettledBridge(transaction)
}

export const useTransaction = (
  transactionId: string,
  { enabled = true }: { enabled?: boolean } = {},
): {
  transaction: BackendTransaction | undefined
  isLoading: boolean
} => {
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const { getTransaction } = useGetTransaction()

  const { data: transaction, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.TRANSACTION, activeOrgId, transactionId],
    queryFn: async () => {
      return getTransaction(transactionId)
    },
    enabled,
    // Settled transactions are immutable — serve from cache and stop polling.
    // A terminal transaction with an unsettled bridge leg keeps polling until
    // the backend settles the bridge.
    refetchInterval: (query) => {
      const transaction = query.state.data
      if (isTransactionSettled(transaction)) return false
      // Give up on bridge settlement once the backend has gone quiet for the
      // cutoff. staleTime stays 0, so a fresh mount still refetches once.
      if (
        transaction &&
        !PENDING_TRANSACTION_STATUSES.includes(transaction.status) &&
        Date.now() - new Date(transaction.updatedAt).getTime() >
          BRIDGE_SETTLEMENT_POLL_CUTOFF_MS
      ) {
        return false
      }
      return REFETCH_TRANSACTIONS
    },
    staleTime: (query) =>
      isTransactionSettled(query.state.data) ? Infinity : 0,
    initialData: () => {
      const queries = compact(
        queryClient.getQueriesData({
          queryKey: [QUERY_KEYS.TRANSACTIONS],
        }),
      ) as [string[], { pages: BackendTransaction[][] | undefined }][]

      const transaction = queries
        .map(([, data]) => {
          if (!data?.pages) return []
          return data.pages.flat()
        })
        .flat()
        .find((transaction) => transaction.id === transactionId)

      return transaction
    },
  })

  return {
    transaction,
    isLoading,
  }
}

export const useOrgTransaction = (
  transactionId: string,
  options?: { enabled?: boolean },
) => {
  const result = useTransaction(transactionId, options)

  if (result.transaction && result.transaction.isPublicTransaction) {
    return { ...result, transaction: undefined }
  }
  return { ...result, transaction: result.transaction }
}

const PAGE_SIZE = 20

export const useTransactions = ({
  smartAccountId,
  smartAccountOrgId,
  hideDust,
  enabled = true,
}: {
  smartAccountId?: string
  smartAccountOrgId?: string
  hideDust?: boolean
  enabled?: boolean
}) => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  const queryClient = useQueryClient()
  const { sendInboundToastNotification } = useNotifications()
  const { user: authUser } = useAuthUser()
  // TODO: Remove dependance on all smart accounts
  const { smartAccounts } = useSmartAccounts({
    includeRootAccount: true,
  })
  const smartAccount = smartAccounts.find(
    (account) => account.id === smartAccountId,
  )
  const hasOrgAccess = smartAccount?.orgId === activeOrgId

  const params = useMemo(() => {
    const params: {
      limit: number
      orgId?: string
      smartAccountId?: string
      hideDust?: boolean
    } = {
      limit: PAGE_SIZE,
    }
    if (activeOrgId && (!smartAccountId || hasOrgAccess)) {
      params['orgId'] = activeOrgId
    } else if (smartAccountOrgId) {
      params['orgId'] = smartAccountOrgId
    }
    if (smartAccountId) {
      params['smartAccountId'] = smartAccountId
    }
    if (hideDust) {
      params['hideDust'] = hideDust
    }

    return params
  }, [activeOrgId, hasOrgAccess, smartAccountId, smartAccountOrgId, hideDust])

  const fetchTransactions = useCallback(
    // TODO: Figure out type here
    async ({ pageParam }: { pageParam: number | unknown }) => {
      if (!activeOrgId && !smartAccountId) {
        throw new Error('No active org or smart account filters')
      }

      const result = await serverGetRequest<{
        transactions: Array<OrgTransaction | OrgAssetTransfer>
        nextPage: {
          statusOrder: 0 | 1
          transactionTime: string
          transferId?: string
        } | null
      }>({
        path: `/api/smart_accounts/transactions/v2`,
        params: {
          ...params,
          nextPage: JSON.stringify(pageParam),
        },
      })

      if (!result?.data) {
        throw new Error('No transactions found')
      }

      const transactions = result.data.transactions

      // Pre-populate individual queued transactions in the cache
      transactions
        .filter(
          (transaction): transaction is OrgTransaction =>
            transaction.isSplitsTransaction,
        )
        .filter((transaction) =>
          QUEUE_TRANSACTION_STATUSES.includes(transaction.status),
        )
        .forEach((transaction) => {
          const queryKey = [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id]
          const queryData = queryClient.getQueryData(queryKey)

          if (!queryData) {
            queryClient.setQueryData(queryKey, transaction)
          }
        })

      // if an inbound transfer is encountered in the last minute, notify the user.
      const earliestTime = new Date(Date.now() - 60 * 1000) // 1 minute
      transactions
        .filter(
          (transaction): transaction is OrgAssetTransfer =>
            !transaction.isSplitsTransaction,
        )
        .filter(
          (transaction) => new Date(transaction.transactionTime) > earliestTime,
        )
        .forEach((transaction) => sendInboundToastNotification(transaction))

      return result.data
    },
    [
      activeOrgId,
      params,
      queryClient,
      sendInboundToastNotification,
      serverGetRequest,
      smartAccountId,
    ],
  )

  const { data, isLoading, fetchNextPage, hasNextPage } =
    useInfiniteQuery<InfiniteTransactionsResponse>({
      queryKey: [QUERY_KEYS.TRANSACTIONS, params],
      queryFn: fetchTransactions,
      refetchInterval: REFETCH_TRANSACTIONS,
      initialPageParam: null,
      getNextPageParam: (lastResponse) => {
        return lastResponse.nextPage
      },
      enabled: !!authUser && enabled,
    })

  return {
    transactions: data?.pages.flatMap((t) => t.transactions) || [],
    hasMore: hasNextPage,
    isInitialLoading: isLoading,
    fetchNextPage,
  }
}

export const useTransactionProperties = (
  transaction?: BackendTransaction,
  hasAccessOverride?: boolean,
) => {
  const connectedAddress = useConnectedAddress()
  const activeOrgId = useActiveOrgId()
  const activeOrgMember = useActiveOrgMember()

  const getMutationPredicate = (
    key: string,
    variableCheck: (variables: unknown) => boolean,
  ) => {
    return (mutation: Mutation<unknown, Error, unknown, unknown>) => {
      // The cache runs this predicate against every mutation, including ones
      // registered without a mutationKey.
      const mutationKey = mutation.options.mutationKey as string[] | undefined
      return (
        !!mutationKey &&
        mutationKey[0] === QUERY_KEYS.TRANSACTION &&
        mutationKey[1] === key &&
        mutationKey[2] === activeOrgId &&
        variableCheck(mutation.state.variables)
      )
    }
  }

  const isSigning = !!useIsMutating({
    predicate: getMutationPredicate(
      MUTATION_KEYS.TRANSACTION_SIGN,
      (variables) => {
        const vars = variables as ApproveTransactionParams
        return vars.transaction.id === transaction?.id
      },
    ),
  })

  const isSubmitting = !!useIsMutating({
    predicate: getMutationPredicate(
      MUTATION_KEYS.TRANSACTION_SUBMIT,
      (variables) => {
        const vars = variables as SubmitTransactionParams
        return vars.transaction.id === transaction?.id
      },
    ),
  })

  const isConfirming = !!useIsMutating({
    predicate: getMutationPredicate(
      MUTATION_KEYS.TRANSACTION_CONFIRM,
      (variables) => {
        const vars = variables as PollSubmittedTransactionParams
        return vars.transactionId === transaction?.id
      },
    ),
  })

  const isUpdatingGas = !!useIsMutating({
    predicate: getMutationPredicate(
      MUTATION_KEYS.UPDATE_GAS_ESTIMATION,
      (variables) => {
        const vars = variables as UpdateGasEstimationParams
        return vars.transactionId === transaction?.id
      },
    ),
  })

  const isOrgTransaction = !!transaction && isTransactionOrg(transaction)

  const { smartAccount, isLoading: isLoadingSmartAccount } =
    useTransactionSmartAccount(transaction)
  const isOrgSmartAccount = smartAccount && !smartAccount.isPublicSmartAccount
  const { passkeyIds, isLoading: isLoadingPasskeys } = useUserPasskeys()

  const hasAccess = hasAccessOverride ?? (isOrgTransaction && isOrgSmartAccount)
  const isLoading = isLoadingSmartAccount || isLoadingPasskeys

  if (isLoading || !hasAccess || !smartAccount || !transaction) {
    return {
      hasAccess,
      canConnect: false,
      canSign: false,
      canSubmit: false,
      canExecute: false,
      hasSigned: false,
      isQueued: false,
      isApproved: false,
      isSubmitted: false,
      isSigning: false,
      isSubmitting: false,
      isConfirming: false,
      isConfirmed: false,
      isUpdatingGas: false,
    }
  }

  const isQueued = QUEUE_TRANSACTION_STATUSES.includes(transaction.status)

  const signaturesNeeded =
    smartAccount.threshold -
    transaction.signers.length -
    transaction.eoaSigners.length

  const canSubmit = isOrgTransaction && transaction.status === 'APPROVED'

  const transactionSignerIds = new Set(
    transaction.signers.map((transactionSigner) => transactionSigner.id),
  )
  const transactionEoaSignerIds = new Set(
    transaction.eoaSigners.map((eoaSigner) => eoaSigner.id),
  )

  const canConnect =
    isQueued &&
    transaction.status !== TransactionStatus.Approved &&
    smartAccount.eoaSigners.length > 0 &&
    !connectedAddress

  const isSmartAccountPasskeySigner = some(smartAccount.signers, (signer) =>
    passkeyIds.has(signer.id),
  )
  const isSmartAccountEoaSigner =
    connectedAddress &&
    some(smartAccount.eoaSigners, (eoaSigner) =>
      Address.equal(eoaSigner.address, connectedAddress),
    )
  const isSmartAccountSigner =
    isSmartAccountPasskeySigner || isSmartAccountEoaSigner

  const canPasskeySign = some(smartAccount.signers, (signer) => {
    if (hasAccessOverride) {
      return true
    }
    return passkeyIds.has(signer.id) && !transactionSignerIds.has(signer.id)
  })
  const canEoaSign =
    connectedAddress &&
    some(smartAccount.eoaSigners, (eoaSigner) => {
      if (hasAccessOverride) {
        return true
      }
      return (
        Address.equal(eoaSigner.address, connectedAddress) &&
        !transactionEoaSignerIds.has(eoaSigner.id)
      )
    })
  const canUserSign = canPasskeySign || canEoaSign

  const canSign = isQueued && transaction.status !== 'APPROVED' && canUserSign

  const canExecute = canSign && signaturesNeeded === 1

  const hasPasskeySigned = transaction.signers.some((signer) =>
    passkeyIds.has(signer.id),
  )
  const hasEoaSigned =
    connectedAddress &&
    transaction.eoaSigners.some((eoaSigner) =>
      Address.equal(eoaSigner.address, connectedAddress),
    )
  const hasSigned = hasPasskeySigned || hasEoaSigned

  const isApproved = transaction.status === 'APPROVED'
  const isSubmitted = SUBMITTED_TRANSACTION_STATUSES.includes(
    transaction.status,
  )
  const isConfirmed = CONFIRMED_TRANSACTION_STATUSES.includes(
    transaction.status,
  )

  const isOwner =
    !transaction.isPublicTransaction &&
    transaction.orgId === activeOrgId &&
    activeOrgMember?.role === OrgMemberRole.Owner
  const canReject =
    !smartAccount?.isPublicSmartAccount &&
    !isSubmitted &&
    transaction.status !== TransactionStatus.Cancelled &&
    (isSmartAccountSigner || isOwner)

  return {
    isLoading,
    hasAccess,
    canConnect,
    canSign,
    canSubmit,
    canExecute,
    hasSigned,
    isQueued,
    isApproved,
    isSubmitted,
    canReject,
    isSigning,
    isSubmitting,
    isConfirming,
    isConfirmed,
    isUpdatingGas,
  }
}

type UpdateGasEstimationParams = {
  transactionId: string
}

export const useUpdateGasEstimation = () => {
  const { serverPutRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const updateGasEstimation = useCallback(
    async (params: UpdateGasEstimationParams) => {
      const response = await serverPutRequest<{
        updated: boolean
        transaction?: BackendTransaction
      }>({
        path: `/api/smart_accounts/transactions/${params.transactionId}/update_gas_estimation`,
        errorMessage: 'Failed to update transaction gas values',
      })

      let transactionResponse
      if (response) {
        if (response.data.updated && response.data.transaction) {
          queryClient.setQueryData(
            [QUERY_KEYS.TRANSACTION, activeOrgId, params.transactionId],
            (transaction: BackendTransaction) => {
              if (transaction) {
                transactionResponse = {
                  ...transaction,
                  ...response.data.transaction,
                }
                return transactionResponse
              }
            },
          )

          return {
            updated: true,
            transaction: response.data.transaction,
          }
        } else {
          return {
            updated: false,
            transaction: undefined,
          }
        }
      }

      return undefined
    },
    [queryClient, serverPutRequest, activeOrgId],
  )

  const updateGasEstimationMutation = useMutation({
    mutationKey: [
      QUERY_KEYS.TRANSACTION,
      MUTATION_KEYS.UPDATE_GAS_ESTIMATION,
      activeOrgId,
    ],
    mutationFn: updateGasEstimation,
  })

  return updateGasEstimationMutation.mutateAsync
}

export const useUpdateTransactionMemo = (): {
  updateMemo: (transaction: OrgTransaction, memo: string) => Promise<void>
  isProcessing: boolean
} => {
  const { isProcessing, serverPutRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()
  const { updateMemo: updateAccountingMemo } = useAccountingUpdates()

  const updateMemo = useCallback(
    async (transaction: OrgTransaction, memo: string) => {
      const result = await serverPutRequest<{ memo: string | undefined }>({
        path: `/api/smart_accounts/transactions/${transaction.id}/memo`,
        params: {
          memo,
        },
        errorMessage: 'Failed to update transaction memo',
      })

      if (result) {
        queryClient.setQueryData(
          [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
          (cachedTransaction: BackendTransaction) => {
            const updatedTransaction = {
              // If the transaction is not in the cache, use the transaction passed in
              ...(cachedTransaction ?? transaction),
              memo,
            }
            return updatedTransaction
          },
        )
        queryClient.setQueriesData(
          {
            queryKey: [QUERY_KEYS.TRANSACTIONS],
            predicate: (query) => {
              return (
                Array.isArray(query.queryKey) &&
                query.queryKey[1]?.orgId === activeOrgId
              )
            },
          },
          (
            oldData:
              InfiniteData<InfiniteTransactionsResponse, unknown> | undefined,
          ) => {
            if (!oldData) return oldData

            const updatedTransactions = oldData.pages.map((page) => {
              return {
                ...page,
                transactions: page.transactions.map((t) =>
                  t.id === transaction.id ? { ...t, memo } : t,
                ),
              }
            })

            return {
              ...oldData,
              pages: updatedTransactions,
            }
          },
        )
        updateAccountingMemo(transaction.id, memo)
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.ORG_ACCOUNTING_ITEMS],
        })
      }
    },
    [queryClient, serverPutRequest, activeOrgId, updateAccountingMemo],
  )

  return {
    updateMemo,
    isProcessing,
  }
}

export const useTxDisabledSmartAccounts = (): OrgSmartAccount[] => {
  const { smartAccounts } = useSmartAccounts()
  const { passkeyIds: userPasskeyIds } = useUserPasskeys()
  const orgMember = useActiveOrgMember()

  return smartAccounts.filter((smartAccount) => {
    return !canCreateTransaction({ smartAccount, orgMember, userPasskeyIds })
  })
}

export const useCanCreateTransaction = (smartAccount?: OrgSmartAccount) => {
  const { passkeyIds: userPasskeyIds } = useUserPasskeys()
  const orgMember = useActiveOrgMember()

  if (!smartAccount) return false

  return canCreateTransaction({ smartAccount, orgMember, userPasskeyIds })
}

export const useCanCreateAnyTransaction = () => {
  const { passkeyIds: userPasskeyIds } = useUserPasskeys()
  const orgMember = useActiveOrgMember()
  const { smartAccounts } = useSmartAccounts()

  return smartAccounts.some((smartAccount) =>
    canCreateTransaction({ orgMember, smartAccount, userPasskeyIds }),
  )
}

export const useCanExecuteNewTransaction = (smartAccount?: OrgSmartAccount) => {
  const { passkeyIds: userPasskeyIds } = useUserPasskeys()

  if (!smartAccount) return false

  return (
    smartAccount.threshold === 1 &&
    isSignerOnSmartAccount({ smartAccount, userPasskeyIds })
  )
}

const canCreateTransaction = ({
  orgMember,
  smartAccount,
  userPasskeyIds,
}: {
  orgMember: OrgMember | undefined
  smartAccount: OrgSmartAccount
  userPasskeyIds: Set<string>
}) => {
  if (smartAccount.role === SmartAccountRole.ROOT) return false

  const isOwner = orgMember?.role === 'OWNER'

  if (isOwner) {
    return true
  }

  return isSignerOnSmartAccount({ smartAccount, userPasskeyIds })
}

const isSignerOnSmartAccount = ({
  smartAccount,
  userPasskeyIds,
}: {
  smartAccount: OrgSmartAccount
  userPasskeyIds: Set<string>
}) => {
  return smartAccount.signers.some((smartAccountSigner) =>
    userPasskeyIds.has(smartAccountSigner.id),
  )
}
